import React, { useState, useEffect } from "react";
import Select from "react-select"; // ✅ react-select import
import api from "../api";
import Loader from "./Loader";

const MaintenanceRequestForm = ({ onRequestAdded }) => {
  const [formData, setFormData] = useState({
    vehicle_no: "",
    request_type: "Temporary",
    details: "",
    replacement_vehicle: "",
    partner_id: "",
  });

  const [handoverOdometer, setHandoverOdometer] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [replacementOptions, setReplacementOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch dropdown data
  useEffect(() => {
    api
      .get("/vehicles/with-partner")
      .then((response) => setVehicles(response.data))
      .catch((err) =>
        console.error("Error fetching vehicles with partner:", err)
      );

    api
      .get("/vehicles/unassigned")
      .then((response) => setReplacementOptions(response.data))
      .catch((err) =>
        console.error("Error fetching unassigned vehicles:", err)
      );
  }, []);

  // Auto-fill partner_id
  useEffect(() => {
    if (formData.vehicle_no) {
      const selectedVehicle = vehicles.find(
        (v) => String(v.vehicle_no) === String(formData.vehicle_no)
      );
      setFormData((fd) => ({
        ...fd,
        partner_id: selectedVehicle ? selectedVehicle.partner_id || "" : "",
      }));
    } else {
      setFormData((fd) => ({ ...fd, partner_id: "" }));
    }
  }, [formData.vehicle_no, vehicles]);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_no) {
      alert("Please select a vehicle.");
      return;
    }

    const selectedVehicle = vehicles.find(
      (v) => String(v.vehicle_no) === String(formData.vehicle_no)
    );

    if (!selectedVehicle) {
      alert("Invalid vehicle selection");
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Save Maintenance Request
      const reqData = {
        ...formData,
        vehicle_no: selectedVehicle.vehicle_no,
      };

      const response = await api.post("/maintenance-requests", reqData);
      console.log("Maintenance request response:", response.data);

      // 2. Save Handover Log **only if replacement_vehicle present**
      if (formData.replacement_vehicle) {
        await api.post("/handover-logs", {
          partner_id: selectedVehicle.partner_id,
          handover_reading: handoverOdometer,
          deposit_received: null,
          payment_mode: null,
          txn_id: null,
          vehicle_no: formData.replacement_vehicle,
        });
      }

      alert("Maintenance request submitted successfully!");
      if (onRequestAdded) onRequestAdded();

      // Reset form
      setFormData({
        vehicle_no: "",
        request_type: "Temporary",
        details: "",
        replacement_vehicle: "",
        partner_id: "",
      });
      setHandoverOdometer("");
    } catch (error) {
      console.error("Error submitting maintenance request:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 p-6 bg-white dark:bg-gray-800 h-full overflow-y-auto"
    >
      {isSubmitting && <Loader />}
      <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
        New Maintenance Request
      </h2>

      <div className="space-y-4">
        {/* Vehicle Number with Searchable Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Vehicle Number
          </label>
          <Select
            options={vehicles.map((v) => ({
              value: v.vehicle_no,
              label: v.vehicle_no,
            }))}
            value={
              formData.vehicle_no
                ? { value: formData.vehicle_no, label: formData.vehicle_no }
                : null
            }
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                vehicle_no: selected ? selected.value : "",
              }))
            }
            placeholder="Search or select a vehicle..."
            isClearable
            className="mt-1"
            isDisabled={isSubmitting}
          />
        </div>

        {/* Request Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Request Type
          </label>
          <select
            name="request_type"
            value={formData.request_type}
            onChange={handleChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 
              dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-indigo-500 
              focus:border-indigo-500 sm:text-sm rounded-md"
            disabled={isSubmitting}
          >
            <option>Temporary</option>
            <option>Permanent</option>
          </select>
        </div>

        {/* Replacement Vehicle with Searchable Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Replacement Vehicle
          </label>
          <Select
            options={replacementOptions.map((v) => ({
              value: v.vehicle_no,
              label: v.vehicle_no,
            }))}
            value={
              formData.replacement_vehicle
                ? {
                    value: formData.replacement_vehicle,
                    label: formData.replacement_vehicle,
                  }
                : null
            }
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                replacement_vehicle: selected ? selected.value : "",
              }))
            }
            placeholder="Search or select replacement vehicle..."
            isClearable
            className="mt-1"
            isDisabled={isSubmitting}
          />
        </div>

        {/* Partner ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Partner ID (Auto-filled)
          </label>
          <input
            type="text"
            name="partner_id"
            value={formData.partner_id || ""}
            readOnly
            className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 
              border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-600 
              dark:text-white sm:text-sm"
          />
        </div>

        {/* Odometer Reading */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Handover Odometer Reading
          </label>
          <input
            type="number"
            value={handoverOdometer}
            onChange={(e) => setHandoverOdometer(e.target.value)}
            placeholder="Enter odometer reading"
            className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 
              border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-800 
              dark:text-white sm:text-sm"
            disabled={isSubmitting}
          />
        </div>

        {/* Details */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Details
          </label>
          <textarea
            name="details"
            value={formData.details}
            onChange={handleChange}
            rows="4"
            className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 
              border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
              focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            disabled={isSubmitting}
          ></textarea>
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-6">
        <button
          type="submit"
          className="w-full flex justify-center py-3 px-4 border border-transparent 
            rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </button>
      </div>
    </form>
  );
};

export default MaintenanceRequestForm;
